import React, { useState, useContext, useCallback } from "react";
import { AuthContext } from "shared/context/auth-context";
import { StoreContext } from "shared/context/store-context";
import { useHttpClient } from "shared/hooks/http-hook";
import { useTranslation } from "react-i18next";
import CustomSelect from "shared/components/FormElements/Select";
import DateSelect from "shared/components/FormElements/DatePicker";
import Button from "shared/components/FormElements/Button";
import LoadingSpinner from "shared/components/UIElements/LoadingSpinner";
import { useForm } from "shared/hooks/form-hook";
import { VALIDATOR_REQUIRE } from "shared/util/validators";
import useReportProcess from "./useReportProcess";
import { getCurrentLanguage } from "shared/utility/lang";

const TrackVehicleReports = () => {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const store = useContext(StoreContext);
  const { sendRequest } = useHttpClient();
  const { isProcessing, filename, error, startProcess, reset } = useReportProcess();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [onlyActiveDays, setOnlyActiveDays] = useState(true);
  const [reportFormat, setReportFormat] = useState("pdf");

  const [formState, inputHandler] = useForm(
    {
      school: { value: "", isValid: false },
      startDate: { value: "", isValid: false },
      endDate: { value: "", isValid: false },
    },
    false,
  );

  const handleSubmit = useCallback(async () => {
    if (!formState.isValid) return;

    setIsSubmitting(true);
    reset();

    try {
      const resp = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/service-report/generate`,
        "POST",
        JSON.stringify({
          schoolId: formState.inputs.school.value,
          startDate: formState.inputs.startDate.value,
          endDate: formState.inputs.endDate.value,
          language: getCurrentLanguage() === "tr" ? "tr" : "en",
          onlyActiveDays,
          format: reportFormat === "docx" ? "docx" : "pdf",
        }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );

      if (resp?.process?.id) {
        startProcess(resp.process.id);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsSubmitting(false);
    }
  }, [formState, sendRequest, auth.token, startProcess, reset, onlyActiveDays, reportFormat]);

  const handleDownload = useCallback(() => {
    if (!filename) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/service-report/download/${filename}`;
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);

    const token = auth.token;
    fetch(url, { headers: { Authorization: "Bearer " + token } })
      .then((res) => {
        if (!res.ok) throw new Error("Download failed");
        return res.blob();
      })
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(
          new Blob([blob], { type: blob.type || "application/pdf" })
        );
        link.href = blobUrl;
        link.click();
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
      })
      .catch(() => {
        window.toast?.error(t("anErrorOccurred"));
      });
  }, [filename, auth.token, t]);

  return (
    <div style={{ padding: "1.5rem" }}>
      <h2
        style={{
          color: "var(--color-primary-surface)",
          marginBottom: "1.5rem",
          borderBottom: "2px solid var(--color-primary-surface)",
          paddingBottom: "0.5rem",
        }}
      >
        {t("serviceReport")}
      </h2>

      {/* Form */}
      {!isProcessing && !filename && (
        <div
          style={{
            backgroundColor: "#fff",
            padding: "2rem",
            borderRadius: "12px",
            border: "1px solid #e0e0e0",
          }}
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "flex-start" }}>
            <div style={{ flex: "0 1 600px", minWidth: "220px" }}>
              <CustomSelect
                options={store.schoolOptions}
                id="school"
                onInput={inputHandler}
                label={t("client")}
                errorText={t("requiredField")}
                initialValid={false}
                validators={[VALIDATOR_REQUIRE()]}
              />
            </div>
            <div style={{ flex: "0 1 200px", minWidth: "180px" }}>
              <DateSelect id="startDate" onInput={inputHandler} label={t("startDate")} />
            </div>
            <div style={{ flex: "0 1 200px", minWidth: "180px" }}>
              <DateSelect id="endDate" onInput={inputHandler} label={t("endDate")} />
            </div>
          </div>

          <div style={{ marginTop: "1rem", display: "flex", flexWrap: "wrap", gap: "1.5rem", alignItems: "center" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", fontSize: "0.9rem" }}>
              <input
                type="checkbox"
                checked={onlyActiveDays}
                onChange={(e) => setOnlyActiveDays(e.target.checked)}
                style={{ width: "16px", height: "16px", cursor: "pointer" }}
              />
              {t("onlyActiveDays")}
            </label>

            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", fontSize: "0.9rem" }}>
              <span style={{ fontWeight: 500 }}>{t("reportFormat")}:</span>
              <label style={{ display: "flex", alignItems: "center", gap: "0.3rem", cursor: "pointer" }}>
                <input
                  type="radio"
                  name="reportFormat"
                  value="pdf"
                  checked={reportFormat === "pdf"}
                  onChange={() => setReportFormat("pdf")}
                  style={{ cursor: "pointer" }}
                />
                PDF
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: "0.3rem", cursor: "pointer" }}>
                <input
                  type="radio"
                  name="reportFormat"
                  value="docx"
                  checked={reportFormat === "docx"}
                  onChange={() => setReportFormat("docx")}
                  style={{ cursor: "pointer" }}
                />
                Word
              </label>
            </div>
          </div>

          <div style={{ marginTop: "1rem" }}>
            <Button type="button" onClick={handleSubmit} disabled={!formState.isValid || isSubmitting}>
              {isSubmitting ? t("pleaseWait") : t("generateReport")}
            </Button>
          </div>
        </div>
      )}

      {/* Processing */}
      {isProcessing && (
        <div
          style={{
            backgroundColor: "#fff",
            padding: "3rem",
            borderRadius: "12px",
            border: "1px solid #e0e0e0",
            textAlign: "center",
            minHeight: "300px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <LoadingSpinner />
          <h3
            style={{
              marginTop: "2rem",
              color: "var(--color-primary-surface)",
              fontSize: "1.5rem",
              fontWeight: "600",
            }}
          >
            {t("reportBeingPrepared")}
          </h3>
          <p style={{ marginTop: "1rem", color: "#666", fontSize: "1rem", maxWidth: "400px" }}>
            {t("pleaseWaitReport")}
          </p>
        </div>
      )}

      {/* Download Ready */}
      {filename && !isProcessing && (
        <div
          style={{
            backgroundColor: "#fff",
            padding: "3rem",
            borderRadius: "12px",
            border: "1px solid #e0e0e0",
            textAlign: "center",
            minHeight: "200px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>&#10004;</div>
          <h3
            style={{
              color: "var(--color-primary-surface)",
              fontSize: "1.5rem",
              fontWeight: "600",
              marginBottom: "1rem",
            }}
          >
            {t("reportReady")}
          </h3>
          <div style={{ display: "flex", gap: "1rem" }}>
            <Button type="button" onClick={handleDownload}>
              {t("downloadReport")}
            </Button>
            <Button type="button" danger onClick={reset}>
              {t("newReport")}
            </Button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && !isProcessing && (
        <div
          style={{
            backgroundColor: "#fff3f3",
            padding: "2rem",
            borderRadius: "12px",
            border: "1px solid #ffcccc",
            textAlign: "center",
            marginTop: filename ? "1rem" : "0",
          }}
        >
          <p style={{ color: "#cc0000", fontWeight: "600" }}>{error}</p>
          <div style={{ marginTop: "1rem" }}>
            <Button type="button" onClick={reset}>
              {t("tryAgain")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackVehicleReports;
